<template>
  <div class="direct-payout-wrapper">
    <p class="banner warning icon" v-if="!isUserAllowed">
      You do not have permission to create a payout. Please contact your administrator.
    </p>
    <section v-else>
      <p>
        Send a crypto payout directly to the recipient’s wallet address.
      </p>
      <transition name="slide-fade">
        <ul class="banner error icon" v-if="errors.length > 0">
          <li v-for="e in errors" :key="e.code">
            {{ e.message }} {{ e.code ? `(${e.code})` : '' }}
          </li>
          <li>If error(s) persists, please contact our <a href="mailto:support@triple-a.io?subject=Crypto payout direct error">support team</a></li>
        </ul>
      </transition>
      <transition name="slide-fade">
        <p class="banner warning icon" v-if="shouldShowKycError">
          The amount you are trying to send requires additional KYC information. Please contact our <a href="mailto:support@triple-a.io?subject=KYC required for direct payout">support team</a> for further instructions.
        </p>
      </transition>

      <h3 v-if="!shouldShowPreview">Step 1 of 2: Specify the recipient and payout amount</h3>
      <transition name="slide-fade">
        <form @submit.prevent="" v-if="!shouldShowPreview" :disabled="isSubmitting">
          <label>
            Send from <span class="req">*</span>
            <span class="tooltip">
              <info-icon size="1x"></info-icon>
              <span>
                <span><strong>Real Balance</strong> refers to balance
                  for real world transaction.</span>
                <span><strong>Test Balance</strong> refers to balance
                  that has no real value and the amount are used
                  only for testing purposes.</span>
              </span>
            </span>
            <vs-select autocomplete v-model="senderBalance" required
              :danger="hasTriedToSubmit && !senderBalance"
              danger-text="Please select the balance to send from">
              <vs-select-group title="Real balance" v-if="realPayoutAccountsList">
                <vs-select-item v-for="(bal, index) in realPayoutAccountsList" :value="bal" :text="`${activeMerchantGetter.merchant_name} - ${bal.available_currency} ${bal.available_amount}`" :key="`bal_${index}`"></vs-select-item>
              </vs-select-group>
              <vs-select-group title="Test balance" v-if="testPayoutAccountList">
                <vs-select-item v-for="(bal, index) in testPayoutAccountList" :value="bal" :text="`${activeMerchantGetter.merchant_name} - ${bal.available_currency} ${bal.available_amount}`" :key="`bal_${index}`"></vs-select-item>
              </vs-select-group>
            </vs-select>
          </label>

          <!-- Amounts -->
          <fieldset>
            <legend>Payout details</legend>
            <ul>
              <li>
                <label>
                  Currency <span class="req">*</span>
                  <vs-select autocomplete v-model="originCur" disabled>
                    <vs-select-item :value="activeMerchantGetter.local_currency" :text="activeMerchantGetter.local_currency" is-selected></vs-select-item>
                  </vs-select>
                </label>
                <label>
                  Sending Amount <small>(Does not include fees)</small><span class="req">*</span>
                  <input type="number" required v-model="originAmt" min="0.00" step="0.01" @input="handleOriginAmtInput()" :disabled="isFetchingRates"
                    :danger="hasTriedToSubmit && !originAmt"
                    :warning="!!originAmt && (originAmt < 0 || originAmt > senderBalance.available_amount)" />
                  <div class="vs-con-loading__container" id="crypto-pyt-loading-sending-amt" :enabled="isFetchingRates"></div>
                  <p v-if="!!originAmt && originAmt > senderBalance.available_amount" class="warning-msg">Amount greater than current balance</p>
                  <p v-else-if="!!originAmt && originAmt < 0">Amount too small</p>
                  <p v-if="hasTriedToSubmit && !originAmt" class="error-msg">Please enter a valid amount</p>
                </label>
              </li>
              <li class="swap">
                <img src="@/assets/icons/swap.svg" alt="swap-vert" />
              </li>
              <li>
                <label>
                  Currency <span class="req">*</span>
                  <vs-select autocomplete v-model="destinationCur" :disabled="isFetchingRates || isTestBalance">
                    <vs-select-item v-for="r in rateList" :value="r.crypto_currency" :text="formatCryptoCurrencyName(r.crypto_currency)" :key="r.crypto_currency"></vs-select-item>
                  </vs-select>
                </label>
                <label>
                  Receiving Amount <span class="req">*</span>
                  <input type="number" required v-model="destinationAmt" min="0.00" :step="isCryptoCurrency(destinationCur) ? '0.00000001' : '0.01'" @input="handleDestAmtInput()" :disabled="isFetchingRates" />
                  <div class="vs-con-loading__container" id="crypto-pyt-loading-receiving-amt" :enabled="isFetchingRates"></div>
                </label>
                <dl class="rate" v-if="rate.crypto_currency">
                  <div>
                    <dt>Estimated Rate
                      <span class="tooltip">
                        <info-icon size="1x"></info-icon>
                        <span>
                          Estimated rate is based on the current market rate.
                          Final rate will be confirmed at the next step.
                        </span>
                      </span>
                    </dt>
                    <dd>{{ `1.00 ${rate.crypto_currency} = ${rate.crypto_to_fiat_exchange_rate} ${rate.fiat_currency}` }}</dd>
                  </div>
                </dl>
                <p>
                  Network fee calculated at the next step.
                </p>
              </li>
            </ul>
            <label>
              Recipient's wallet address <span class="req">*</span>
              <vs-input
                type="text"
                required
                v-model="walletAddress"
                placeholder="Enter the recipient's wallet address"
                :danger="hasTriedToSubmit && (!walletAddress || isWalletAddressInvalid)"
                danger-text="Invalid address, ensure the address corresponds to the selected network"></vs-input>
            </label>
            <label>
              Remarks
              <vs-input type="text" v-model="remarks"></vs-input>
            </label>
          </fieldset>

          <!-- Recipient details -->
          <fieldset>
            <legend>Recipient details</legend>
            <p class="banner">The user information is collected for compliance purposes.
              Triple-A will <b>not</b> contact the user.</p>
            <label>
              Full name <span class="req">*</span>
              <vs-input type="text" required v-model="fullName"
                :danger="hasTriedToSubmit && !fullName"
                danger-text="Please enter the recipient's full name"></vs-input>
            </label>
            <label>
              Recipient's email address <span class="req">*</span>
              <vs-input type="email" required v-model="email" :danger="hasTriedToSubmit && !email" danger-text="Please enter the recipient's email address"></vs-input>
            </label>
            <label>
              Country <span class="req">*</span>
              <vs-select autocomplete v-model="country" required
                :danger="hasTriedToSubmit && !country"
                danger-text="Please select the recipient's country">
                <vs-select-item v-for="country in countryList" :value="country.code" :text="country.name" :key="country.code"></vs-select-item>
              </vs-select>
            </label>
          </fieldset>

          <button class="btn primary" @click="handlePreparePayout()">Get a quote</button>
        </form>
      </transition>

      <h3 v-if="shouldShowPreview">Step 2 of 2: Confirm payout</h3>
      <transition name="slide-fade">
        <article v-if="shouldShowPreview" class="preview-wrapper">
          <transition name="slide-fade">
            <p class="banner success icon" v-if="shouldShowSuccess">Payout submitted!</p>
          </transition>
          <dl>
            <div v-if="!shouldShowSuccess" class="timer">
              <dt>Quote expires in</dt>
              <dd v-if="remainingQuoteLife">{{ formatToMinute(remainingQuoteLife) }}</dd>
              <dd v-else-if="remainingQuoteLife === null"></dd>
              <dd v-else class="expired">Expired</dd>
            </div>
            <div>
              <dt>Deducted Fiat amount (incl. fees)</dt>
              <dd class="key-amt">{{ `${payout.debit_amount} ${payout.payout_currency}` }}</dd>
              <dd class="divider" v-if="!isNetworkFeePaidBySender && !isTransactionFeePaidBySender"></dd>
            </div>
            <!-- <div class="detail">
              <dt>Merchant Spread
                <span class="tooltip">
                  <info-icon size="1x"></info-icon>
                  <span>
                    <span><strong>Spread</strong> refers to merchant fee. It is deducted from the total deducted amount.</span>
                  </span>
                </span>
              </dt>
              <dd>- {{ payout.spread_amount }} {{ payout.payout_currency }}</dd>
            </div> -->
            <div v-if="isNetworkFeePaidBySender" class="detail">
              <dt>Network fee</dt>
              <dd>{{ `${payout.network_fee_crypto_amount} ${payout.crypto_currency}` }}</dd>
            </div>
            <div class="detail" v-if="isTransactionFeePaidBySender">
              <dt>Transaction fee</dt>
              <dd>{{ `${payout.fiat_amount_fee + payout.vat_fiat_amount} ${payout.payout_currency}` }}</dd>
              <dd class="divider"></dd>
            </div>
            <div>
              <dt>Payout amount</dt>
              <dd>{{ `${payout.withdraw_amount} ${payout.payout_currency}` }}</dd>
            </div>
            <div>
              <dt class="fee-title">Fees paid by recipient</dt>
            </div>
            <div class="detail" v-if="!isTransactionFeePaidBySender">
              <dt>Transaction fee</dt>
              <dd>{{ `${payout.fiat_amount_fee + payout.vat_fiat_amount} ${payout.payout_currency}` }}</dd>
            </div>
            <div class="detail" v-if="!isNetworkFeePaidBySender">
              <dt>Network fee</dt>
              <dd>{{ `${payout.network_fee_crypto_amount} ${payout.crypto_currency}` }}</dd>
              <dd class="divider"></dd>
            </div>
            <div class="receive-amt">
              <dt>Recipient receives</dt>
              <dd class="key-amt">{{ `${payout.net_crypto_amount} ${payout.crypto_currency}` }}</dd>
              <dd class="rate">{{ `1 ${payout.crypto_currency} = ${payout.actual_exchange_rate} ${payout.payout_currency}` }}</dd>
              <dd>{{ payout.payee_email }}</dd>
              <dd>
                <span
                  class="feather-icon select-none relative cursor-pointer"
                  @click="copy(payout.crypto_address)">
                  {{ payout.crypto_address }}
                  <copy-icon size="0.5x" class="custom-class pl-2"></copy-icon>
                  <span
                    class="p-2 copied-msg"
                    v-if="copiedValue == payout.crypto_address">
                    <small>Copied!</small>
                  </span>
                </span>
              </dd>
            </div>
            <div>
              <dt>Payout reference</dt>
              <dd>{{ payout.payout_reference }}</dd>
            </div>
            <div class="remarks">
              <dt>Remarks</dt>
              <dd v-if="payout.payee_remarks">{{ payout.payee_remarks }}</dd>
              <dd v-else>/</dd>
            </div>
          </dl>
          <div class="action-group" v-if="!shouldShowSuccess">
            <button class="btn icon-btn cross error" @click="handleBack()">Cancel</button>
            <button class="btn icon-btn confirm" @click="confirmPayout()" v-if="remainingQuoteLife > 0">Confirm payout</button>
            <button class="btn icon-btn reset" @click="refreshQuote()" v-else>Refresh</button>
          </div>
          <div class="action-group" v-else>
            <button class="btn primary" @click="handleCreateAnotherPayout()">Create another payout</button>
            <router-link to="/crypto-payout-admin" class="btn link-style">View payouts history</router-link>
          </div>
        </article>
      </transition>

    </section>

    <!-- payout creaton OTP dialog -->
    <otp-prompt
      :login="$store.state.activeUser.email"
      :active.sync="shouldShowOtpPrepare"
      otpRequired
      @verify="preparePayout($event)" />

  </div>
</template>

<script>
import { InfoIcon, CopyIcon } from 'vue-feather-icons';
import { COUNTRIES } from '../constants';
import { mapGetters } from 'vuex';
import { debounce } from '../plugins/debounceHelper';
import OtpPrompt from '@/components/OtpPrompt';



export default {
  name: 'CryptoPayoutDirect',
  // Your component's JavaScript logic goes here
  components: {
    InfoIcon,
    CopyIcon,
    OtpPrompt,
  },
  data() {
    return {
      senderBalance: '',
      realPayoutAccountsList: [],
      testPayoutAccountList: [],
      // recipient details
      walletAddress: '',
      fullName: '',
      email: '',
      country: '',
      // quote timer
      quoteTimer: null,
      quoteExpiryDate: null,
      remainingQuoteLife: null,

      // payout amounts
      originCur: '',
      originAmt: '',
      destinationCur: '',
      destinationAmt: '',
      remarks: '',
      rate: {
        fiat_currency: '',
        crypto_currency: '',
        crypto_to_fiat_exchange_rate: '',
      },
      rateRefreshTimeout: null,
      rateRefreshFrequency: 60000, // 1 minute
      shouldStopRefreshing: false,

      payout: {},
      isNetworkFeePaidBySender: false,

      rateList: [],
      countryList: COUNTRIES,
      activatedCurrencies: ['BTC', 'ETH', 'LNBC', 'PYUSD', 'PYUSD_SOL', 'USDC', 'USDC_ARB', 'USDC_POLY', 'USDC_SOL', 'USDT', 'USDT_ARB', 'USDT_POLY', 'USDT_SOL', 'USDT_TRC20'],

      errors: [],
      isWalletAddressInvalid: false,

      // 2FA
      isUserAllowed: false,
      is2FaEnabled: false, // 2FA
      is2FaVerified: false, // verifiaction status
      shouldShowOtpPrepare: false, // otp for payout creation

      // flags
      isFetchingRates: false, // flag to show loading spinner
      shouldShowKycError: false,
      shouldShowSuccess: false,
      shouldShowPreview: false,
      isSenderDriven: true,
      hasTriedToSubmit: false,
      isSubmitting: false,
      copiedValue: '',
    };
  },
  mounted() {
    // cancel any existing payout, if any
    // if we cannot find the payoiut to cancel, it will automatically cancel itself after it expires (1min)
    if (this.payout) {
      this.cancelPayout();
    }
    this.checkRights();
    this.check2Fa();
    // init the spinners
    this.$vs.loading({
      container: '#crypto-pyt-loading-receiving-amt',
      scale: 0.7,
      background: 'transparent'
    });
    this.$vs.loading({
      container: '#crypto-pyt-loading-sending-amt',
      scale: 0.7,
      background: 'transparent'
    });
    // get exchange rates
    this.getExchangeRate();

    if (this.payout && this.payout.created_at) {
      // quote already exists, init the quote timer
      this.initQuoteTimer();
    }
  },
  created() {
    this.debouncedWatchDestAmt = debounce(() => {
      this.refreshOriginAmt();
    }, 1000);
    this.debouncedWatchOriginAmt = debounce(() => {
      this.refreshDestinationAmt();
    }, 1000);
  },
  beforeDestroy() {
    this.killRateRefreshTimer();
    this.shouldStopRefreshing = true;
    this.killQuoteLifeTimer();
  },
  methods: {
    /**
     * Adds the rates of currency with their networks (USDT_TRC20 for example) to the rate list
     */
    addNetworksToRateList() {
      this.activeMerchantGetter.accounts.forEach((acc) => {
        // get currencies from the accounts, filtered by local currency and settlement type
        if (acc.local_currency === this.originCur && acc.settlement_type === 'localcurrency') {
          const splitCur = acc.crypto_currency.split('_'); // index 0 is the currency, index 1 is the network
          if (splitCur[1] && !this.rateList.find((rate) => rate.crypto_currency === acc.crypto_currency)) {
            const r = this.rateList.find((rate) => rate.crypto_currency === splitCur[0]);
            if (r) {
              this.rateList.push({
                crypto_currency: acc.crypto_currency,
                crypto_to_fiat_exchange_rate: r.crypto_to_fiat_exchange_rate,
                fiat_currency: r.fiat_currency,
              });
            }
          }
        }
      });
    },

    getExchangeRate() {
      // prevent empty values and a new request if one is already in progress
      if (!this.activeMerchantGetter.merchant_key || !this.originCur || this.isFetchingRates) return;
      //get the exchange rate
      const mkey = this.activeMerchantGetter.merchant_key;
      this.isFetchingRates = true;
      this.$store.dispatch('getMerchantPaymentRequestToken', mkey).then((response) => {
        this.$store.dispatch('getPayoutExchangeRate', {
          token: response.data.access_token,
          origin_currency: this.originCur,
        }).then((res) => {
          this.rateList = [];
          for (let rate of res.data) {
            if (this.isSupportedCryptoCurrency(rate.crypto_currency)) {
              this.rateList.push(rate);
            }
          }
          this.addNetworksToRateList();
          if (this.isTestBalance) {
            this.appendTestBtcRate();
          }
          this.isFetchingRates = false;
          // if we have a rate, update it
          if (this.rate && this.rate.crypto_currency) {
            this.rate = this.rateList.find((rate) => rate.crypto_currency === this.destinationCur && rate.fiat_currency === this.originCur);
          }
          this.resetRefreshTimer();
        }).catch((err) => {
          console.error('error while fetching rate', err);
          this.handleError(err);
          this.isFetchingRates = false;
          this.scrollToTop();
          this.resetRefreshTimer();
        });
      }).catch((error) => {
        console.error('error while getting token', error);
        this.handleError(error);
        this.scrollToTop();
        this.isFetchingRates = false;
        this.resetRefreshTimer();
      });
    },

    preparePayout(event) {
      const otp = event ? event.otp : false;      //get the quote / prepare payout
      this.hasTriedToSubmit = true;
      this.isSubmitting = true;
      this.errors = [];
      this.isWalletAddressInvalid = false;

      if (!this.isFormValid()) {
        return;
      } else {
        this.sendGetCryptoPayoutDirectQuoteEvent();
        // prepare the payout
        const token = this.$store.getters.activeUserGetter.userToken;
        const mkey = this.$store.getters.activeMerchantGetter.merchant_key;
        const payload = {
          token: token,
          merchant_key: mkey,
          merchant_name: this.fullName,
          email: this.email,
          country: this.country,
          address: this.walletAddress,
          // amounts added later based on mode
          withdraw_currency: this.originCur,
          crypto_currency: this.destinationCur,
          remarks: this.remarks,
        };
        if (this.isSenderDriven) {
          payload.withdraw_amount = parseFloat(this.originAmt);
        } else {
          payload.crypto_amount = parseFloat(this.destinationAmt);
        }
        if (otp && this.is2FaEnabled) {
          payload.otp = otp;
        }

        // use the same as in crypto-payout-admin
        this.$recaptchaLoaded().then(() => {
          this.$recaptcha('browser_access').then((token) => {
            payload.recaptchaToken = token;
            this.$store.dispatch('createCryptoPayoutDirect', payload).then((response) => {
              this.errors = [];
              this.shouldShowKycError = false;
              this.killRateRefreshTimer();
              this.getPayoutDetails(response.data.payout_reference);
              this.sendGetCryptoPayoutDirectQuoteSuccessEvent();
            }).catch((error) => {
              console.error('error while preparing payout', error);
              this.isSubmitting = false;
              this.handleError(error);
              this.scrollToTop();
            });
          });
        });
      }
    },

    cancelPayout() {
      if (!this.payout) return;
      //cancel the payout
      const pytId = this.payout.payout_reference ? this.payout.payout_reference : this.payout.id;
      this.errors = [];
      if (!pytId) return;

      this.sendCreateCryptoPayoutDirectCancelEvent();

      // use the same as in crypto-payout-admin
      this.$recaptchaLoaded().then(() => {
        this.$recaptcha('browser_access').then((token) => {
          const data = {};
          data.operator = this.$store.getters.activeUserGetter.email;
          data.merchantKey = this.activeMerchantGetter.merchant_key;
          data.recaptchaToken = token;
          data.payoutReference = pytId;
          this.payoutCancellation(pytId, data).then(() => {
            this.shouldShowPreview = false;
            this.shouldShowSuccess = false;
            this.sendCreateCryptoPayoutDirectCancelSuccessEvent();
          }).catch((error) => {
            if (this.remainingQuoteLife > 0) {
              // only consider it an error if the payout is cancellable
              this.handleError(error);
              this.scrollToTop();
            }
          });
        });
      });
    },

    confirmPayout(event) {
      const otp = event ? event.otp : false;
      //confirm the payout
      const pytId = this.payout ? this.payout.payout_reference : '';
      this.errors = [];
      if (!pytId) return;
      this.shouldStopRefreshing = true;
      this.sendCreateCryptoPayoutDirectEvent();
      this.killRateRefreshTimer();
      this.$recaptchaLoaded().then(() => {
        this.$recaptcha('browser_access').then((recaptchaToken) => {
          const payload = {
            token: this.$store.getters.activeUserGetter.userToken,
            payout_reference: pytId,
            recaptchaToken: recaptchaToken,
            merchant_key: this.activeMerchantGetter.merchant_key,
          };
          if (otp && this.is2FaEnabled) {
            payload.otp = otp;
          }
          this.$store.dispatch('confirmCryptoPayout', payload).then(() => {
            this.shouldShowPreview = true;
            this.shouldShowSuccess = true;
            this.killQuoteLifeTimer();
            this.sendCreateCryptoPayoutDirectSuccessEvent();
          }).catch((error) => {
            if (error.response && error.response.data && error.response.data.message === 'quote_expired') {
              this.errors.push({
                message: 'Quote expired, please refresh or create a new one',
                code: 409
              });
            } else {
              this.handleError(error);
            }
            this.scrollToTop();
          });
        }).catch((error) => {
          console.error('error while getting token', error);
          this.handleError(error);
          this.scrollToTop();
        });
      });
    },

    checkRights() {
      //check the permission of the user in the default selected merchant
      const currentActiveMerchantId = this.activeMerchantGetter && this.$store.getters.activeMerchantGetter.id;
      //determine to show if the user has permission to create a payout or not
      this.isUserAllowed = this.isAllowed(
        this.$store.getters.currentActiveEntityAdminGetter,
        currentActiveMerchantId,
        'payout_links',
        'W'
      );
    },

    check2Fa() {
      //check if the user has 2FA enabled
      this.is2FaEnabled = this.is2faEnabled(
        this.$store.getters.activeMerchantGetter,
        'manual_payout'
      );
    },

    triggger2FaOnCreation() {
      //trigger the 2FA dialog for prepare payout
      this.shouldShowOtpPrepare = true;
    },

    getApiIDFromCurrency(currency) {
      try {
        const acc = this.$store.getters.activeMerchantGetter.accounts.find((acc) => {
          return (acc.crypto_currency === currency);
        });
        return acc.api_id;
      } catch (e) {
        return null;
      }
    },

    getPayoutDetails(pytId) {
      //get the payout details
      const token = this.$store.getters.activeUserGetter.userToken;
      this.errors = [];
      if (!token || !pytId) return;
      const payload = {
        payout_reference: pytId,
        merchant_key: this.activeMerchantGetter.merchant_key,
      };
      this.$store.dispatch('fetchMerchantPayouts', payload).then((response) => {
        if (response.data.data.length < 1) {
          this.errors.push({
            message: 'Payout not found',
            code: 404
          });
          return;
        }
        this.payout = response.data.data[0];
        this.isSubmitting = false;
        this.shouldShowPreview = true;
        this.initQuoteTimer();
      }).catch((error) => {
        console.error('error while getting payout details', error);
        this.handleError(error);
        this.scrollToTop();
      });
    },

    initQuoteTimer() {
      //init the quote timer
      const creationDate = new Date(this.payout.created_at);
      this.quoteExpiryDate = new Date(creationDate.setMinutes(creationDate.getMinutes() + 1));
      this.remainingQuoteLife = null;
      if (this.quoteTimer) clearInterval(this.quoteTimer); // cleanup
      this.quoteTimer = setInterval(() => {
        const now = new Date();
        const seconds = Math.floor((this.quoteExpiryDate.getTime() - now.getTime()) / 1000);
        this.remainingQuoteLife = seconds;
        if (seconds <= 0) {
          clearInterval(this.quoteTimer);
          this.remainingQuoteLife = 0;
        }
      }, 1000);
    },

    resetRefreshTimer() {
      // reset timer to 1 minute
      this.killRateRefreshTimer();
      if (this.shouldStopRefreshing) { return; }
      this.rateRefreshTimeout = setTimeout(() => {
        this.getExchangeRate();
      }, this.rateRefreshFrequency);
    },

    isFormValid() {
      let isValid = true;
      if (!this.senderBalance || !this.walletAddress || !this.fullName || !this.email || !this.country || !this.originAmt || !this.destinationAmt) {
        isValid = false;
        this.errors.push({
          message: 'Please fill in all required fields',
          code: 400
        });
      }
      return isValid;
    },

    isSupportedCryptoCurrency(currency) {
      //check if the currency is supported
      return this.activatedCurrencies.includes(currency);
    },

    handleBack() {
      // cancel payout
      this.cancelPayout();
      //handle the back button
      this.shouldShowPreview = false;
      // authorise rate refresh
      this.shouldStopRefreshing = false;
      this.getExchangeRate();
    },

    refreshQuote() {
      //refresh the quote
      // Since we don't when the payout will really be cancelled by the cron job, we will cancel and re-create the payout
      // This solutions does pollute the payout history with cancelled payouts, but it is an accepted risk
      return [
        this.cancelPayout(),
        this.preparePayout()
      ];
    },

    handlePreparePayout() {
      if (this.is2FaEnabled) {
        this.triggger2FaOnCreation();
      } else {
        this.preparePayout();
      }
    },

    handleCreateAnotherPayout() {
      // reset Data
      this.destinationAmt = '';
      this.destinationCur = '';
      this.originAmt = '';
      this.reference = '';
      this.remarks = '';
      this.walletAddress = '';
      this.fullName = '';
      this.country = '';
      this.rate = {
        fiat_currency: '',
        crypto_currency: '',
        crypto_to_fiat_exchange_rate: '',
      };

      //handle the send another payout button
      this.shouldShowSuccess = false;
      this.shouldShowPreview = false;

      // restart rate fetching
      this.shouldStopRefreshing = false;
      this.getExchangeRate();

      this.killQuoteLifeTimer();
    },

    handleDestinationCurChange() {
      if (!this.destinationCur || !this.rateList) return;
      //handle the destination currency change
      this.rate = this.rateList.find((rate) => rate.crypto_currency === this.destinationCur);
    },

    handleError(e) {      
      //handle the error
      if (e.response.status === 409 && e.response.data.message === 'proof_of_identity_required') {
        this.shouldShowKycError = true;
        return;
      }
      if (!e.response || !e.response.data) {
        this.errors.push({
          message: e.message,
          code: e.status
        });
        return;
      } else if (e.response.data.errors) {
        e.response.data.errors.forEach(err => {
          if (e.response.data.message === 'validation_error' && err.path === 'address') {
            this.isWalletAddressInvalid = true;
          }
          if (err.message === 'balance_short') {
            this.errors.push({
              message: 'Insufficient balance. Please try a smaller amount or select a different balance to send from.',
              code: e.response.status
            });
          } else {
            this.errors.push({
              message: `${err.message} - ${this.parsePath(err.path)}`,
              code: e.response.status
            });
          }
        });

      } else {
        if (e.response.data.message === 'network_fee_too_big') {
          this.errors.push({
            message: 'Network fee too high, please try again later',
            code: e.response.status
          });
        } else {
          this.errors.push({
            message: e.response.data.message,
            code: e.response.status
          });
        }
      }
    },

    parsePath(path) {
      //parse the path
      switch (path) {
        case 'withdraw_currency':
          return 'Sending currency';
        case 'withdraw_amount':
          return 'Sending amount';
        case 'crypto_currency':
          return 'Receiving currency';
        case 'crypto_amount':
          return 'Receiving amount';
        case 'address':
          return 'Recipient\'s wallet address';
        default:
          return path;
      }
    },

    formatToMinute(seconds) {
      //format the seconds to minutes
      const minutes = Math.floor(seconds / 60);
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      const remainingSeconds = seconds % 60;
      const formattedSeconds = remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds;
      return `${formattedMinutes}:${formattedSeconds}`;
    },

    setRealPayoutBalances() {
      //set the real payout balance, hiding the custody accounts
      this.realPayoutAccountsList = this.activeMerchantGetter.balances.filter((item) => item.type === 'real' && !this.isCryptoCurrency(item.available_currency));
    },

    setTestPayoutBalances() {
      //set the test payout balance
      this.testPayoutAccountList = this.activeMerchantGetter.balances.filter((item) => item.type === 'test');
    },

    copy(str) {
      this.copyToClipboard(str);
      this.copiedValue = str;
      setTimeout(() => {
        this.copiedValue = '';
      }, 1000);
    },

    scrollToTop() {
      //scroll to the top of the page
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },

    handleDestAmtInput() {
      //handle the destination amount input
      this.isSenderDriven = false;
      this.refreshOriginAmt();
    },

    handleOriginAmtInput() {
      //handle the origin amount input
      this.isSenderDriven = true;
      this.refreshDestinationAmt();
    },

    refreshDestinationAmt() {
      //refresh the destination amount
      if (!this.originAmt || !this.rate || !this.rate.crypto_currency || !this.rate.crypto_to_fiat_exchange_rate) return;
      const cryptoAmt = this.originAmt / this.rate.crypto_to_fiat_exchange_rate;
      if (this.isCurrencyFiatFormat(this.rate.crypto_currency)) {
        this.destinationAmt = cryptoAmt.toFixed(2);
      } else {
        this.destinationAmt = cryptoAmt.toFixed(8);
      }
    },

    refreshOriginAmt() {
      //refresh the origin amount
      if (!this.destinationAmt || !this.rate || !this.rate.crypto_currency || !this.rate.crypto_to_fiat_exchange_rate) return;
      this.originAmt = (this.destinationAmt * this.rate.crypto_to_fiat_exchange_rate).toFixed(2);
    },

    appendTestBtcRate() {
      const btcRate = this.rateList.find((rate) => rate.crypto_currency === 'BTC');
      if (!btcRate) {
        return;
      } else {
        const testBtcRate = this.rateList.find((rate) => rate.crypto_currency === 'testBTC');
        if (!testBtcRate) {
          // append testBTC rate to the rate list
          const testBTCrate = {
            crypto_currency: 'testBTC',
            crypto_to_fiat_exchange_rate: btcRate.crypto_to_fiat_exchange_rate,
            fiat_currency: btcRate.fiat_currency,
            fiat_currency_name: btcRate.fiat_currency_name,
          };
          this.rateList.push(testBTCrate);
        }
        else {
          testBtcRate.crypto_to_fiat_exchange_rate = btcRate.crypto_to_fiat_exchange_rate;
        }
      }
    },

    killRateRefreshTimer() {
      //kill the rate refresh timer
      if (this.rateRefreshTimeout) clearTimeout(this.rateRefreshTimeout);
    },

    killQuoteLifeTimer() {
      //kill the quote life timer
      if (this.quoteTimer) clearInterval(this.quoteTimer);
    },

  },
  computed: {
    ...mapGetters([
      'activeMerchantGetter',
    ]),
    isTestBalance() {
      return !!this.senderBalance && this.senderBalance.type === 'test';
    },
  },
  watch: {
    activeMerchantGetter: {
      immediate: true,
      handler() {
        this.checkRights();
        this.check2Fa();
        if (this.payout && (this.payout.payout_reference || this.payout.id)) {
          this.cancelPayout();
          this.shouldShowPreview = false;
          this.shouldShowSuccess = false;
        }

        this.setRealPayoutBalances();
        this.setTestPayoutBalances();
        this.originCur = this.activeMerchantGetter.local_currency;
        this.getExchangeRate();
        // amt will be refreshed as the rate changes
      },
    },

    senderBalance(newVal) {
      if (newVal && newVal.type === 'test') {
        this.appendTestBtcRate();
        this.destinationCur = 'testBTC';
      }
    },

    destinationCur: {
      immediate: true,
      handler() {
        this.handleDestinationCurChange();
      },
    },

    destinationAmt(...args) {
      this.debouncedWatchDestAmt(...args);
    },

    rate: {
      deep: true,
      handler() {
        if (this.isSenderDriven) { // if sending amount is the dominant amount
          this.refreshDestinationAmt();
        } else {
          this.refreshOriginAmt();
        }
      },
    },
    originAmt(...args) {
      this.debouncedWatchOriginAmt(...args);
    },
    originCur: {
      handler() {
        this.getExchangeRate();
      },
    },
    isFetchingRates(newVal) {
      // show loading spinner
      const ID = this.isSenderDriven ? '#crypto-pyt-loading-receiving-amt' : '#crypto-pyt-loading-sending-amt';
      if (newVal) {
        try {
          this.$vs.loading({
            container: ID,
            scale: 0.7,
            background: 'transparent'
          });
        }
        catch (e) {
          console.error('error while showing loading spinner', e);
        }
      } else {
        try {
          this.$vs.loading.close('#crypto-pyt-loading-receiving-amt > .con-vs-loading');
          this.$vs.loading.close('#crypto-pyt-loading-sending-amt > .con-vs-loading');
        }
        catch (e) {
          console.error('error while closing loading spinner', e);
        }
      }
    },

    payout: {
      immediate: true,
      deep: true,
      handler() {
        // transaction fee
        if (this.payout && (this.payout.debit_amount === this.payout.withdraw_amount
          || this.payout.debit_amount + this.payout.spread_amount === this.payout.withdraw_amount)) {
          this.isTransactionFeePaidBySender = false;
        } else {
          this.isTransactionFeePaidBySender = true;
        }

        // network fee
        if (this.payout && (this.payout.net_crypto_amount === this.payout.crypto_amount)) {
          this.isNetworkFeePaidBySender = true;
        } else {
          this.isNetworkFeePaidBySender = false;
        }
      },
    },
  },
};
</script>

<style lang="scss">
.direct-payout-wrapper {
  & > .banner.warning {
    margin-top: 24px;
    max-width: 850px;
  } 
  &>section {
    h3 {
      color: $color-text;
    }

    // request form CSS
    &>form {
      display: flex;
      flex-direction: column;
      align-items: center;
      max-width: 500px;

      &>label {
        display: block;
        width: 100%;
        margin-top: $margin-m;

        &>div {
          width: 100%;
          max-width: 100%;
        }
      }

      input[disabled] {
        color: rgb(80, 80, 80);
      }

      fieldset {
        border: none;
        max-width: 100%;

        legend {
          font-size: 1.4rem;
          font-weight: 500;
          color: $color-text;
          padding-top: $margin-l;
        }

        &>label {
          display: block;
          width: 100%;
          margin-top: $margin-m;

          span.req {
            color: $color-error;
          }

          div {
            width: 100%;
            max-width: 100%;
          }
        }

        ul {
          margin-top: 12px;

          li {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;

            label {
              position: relative;

              &:first-of-type {
                width: 120px;
              }

              &:last-of-type {
                width: calc(100% - 120px - 12px);
              }

              div {
                width: 100%;
                max-width: 100%;
              }

              &>input {
                width: 100%;
                max-width: 100%;
                padding: 0.7rem;
                font-size: 1rem;
                border: 1px solid rgba(0, 0, 0, 0.2);
                border-radius: 5px;
              }
            }
          }

          dl {
            width: 100%;
            display: flex;

            div {
              display: flex;
              justify-content: space-between;
              width: 100%;
            }
          }

          p {
            display: block;
            width: 100%;
            margin-top: $margin-s;
            color: $color-primary;
          }
        }

        &>p {
          margin-top: $margin-s;
        }
      }

      .swap {
        display: flex;
        justify-content: center;
        margin-top: $margin-m;
        margin-bottom: $margin-s;

        img {
          width: 36px;
          height: 36px;
        }
      }

      &>button {
        width: 200px;
        height: 40px;
        margin-top: $margin-l;
      }

      dl {
        margin-top: $margin-s;
      }

      input[warning] {
        border-color: orange;
      }

      .warning-msg {
        color: orange;
        font-size: 0.8rem;
        margin-top: $margin-xs;
      }

      .error-msg {
        color: $color-error;
        font-size: 0.8rem;
        margin-top: $margin-xs;
      }
    }

    &>p {
      font-size: 1.2rem;
      font-weight: 400;
      color: $color-text;
      padding-top: $margin-l;
      margin-bottom: $margin-l;
    }

    .banner {
      font-size: medium;
      font-weight: 400;
      max-width: 850px;
    }

    .preview-wrapper {
      max-width: 500px;

      p.banner {
        margin-bottom: $margin-s;
        margin-top: $margin-m;
      }

      dl {
        max-width: 100%;

        div {
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          font-size: medium;
          margin-top: $margin-s;

          &.timer {
            font-weight: 500;
            font-size: large;
            color: $color-text;
            margin-bottom: $margin-xs;
            margin-top: $margin-m;

            dd {
              font-weight: bold;

              &.expired {
                text-transform: capitalize;
                color: $color-error;
              }
            }
          }

          &.detail {
            font-size: small;
            padding-left: $margin-l;
          }

          &.receive-amt {
            margin-bottom: $margin-l;

            dd {
              margin-bottom: $margin-s;
            }
          }

          &.remarks {
            margin-top: $margin-m;

            dd {
              font-size: small;
            }
          }

          &.detail>dd.divider::after {
            left: calc(72px - 24px);
          }

          dt.fee-title {
            font-size: small;
          }

          dd {
            &:not(:first-of-type) {
              text-align: end;
              width: 100%;
              word-wrap: break-word;
            }

            &.rate {
              color: $color-primary;
              font-size: small;
            }

            &.divider {
              padding-bottom: $margin-m;
              margin-top: $margin-m;
              margin-bottom: $margin-l;
              position: relative;
              width: 100%;

              &::after {
                display: block;
                position: relative;
                content: '';
                border: none;
                border-bottom: solid 1px $color-text-light;
                width: 300px;
                top: 12px;
                left: 72px;
              }
            }

            &.key-amt {
              font-weight: bold;
            }

            .copied-msg {
              position: absolute;
              text-align: center;
              background-color: #2774b1;
              border-radius: 5px;
              color: #ffffff;
              // top: 50%;
              right: -64px;
              z-index: 2;
            }
          }

        }
      }

      .action-group {
        display: flex;
        justify-content: space-between;
        margin-top: $margin-m;

        button {
          width: 200px;
          height: 40px;

          &.error {
            background-color: $color-error;
            color: #ffffff;
          }

          &.confirm {
            background-color: $color-success;
            color: #ffffff;
          }
        }

        .link-style:hover {
          text-decoration: underline;
        }
      }
    }

    .tooltip {
      position: relative;
      display: inline-block;

      svg {
        margin-left: 6px;
        width: 12px;
        height: 12px;
      }

      &>span {
        visibility: hidden;
        display: flex;
        flex-direction: column;
        background-color: lightslategray;
        width: 250px;
        padding: 10px;
        border-radius: 5px;
        color: #ffffff;
        font-size: 10px;
        text-align: center;
        position: absolute;
        z-index: 1;
        left: 105%;
        top: -20px;
      }

      &[enabled]>span {
        visibility: visible;
      }

      &:hover {
        &>span {
          visibility: visible;
        }
      }
    }
  }
}

#crypto-pyt-loading-sending-amt,
#crypto-pyt-loading-receiving-amt {
  height: 24px;
  width: 24px;
  min-height: 24px;
  position: absolute;
  right: 0px;
  top: 28px;
  background-color: transparent;
  margin-right: 6px;


  &>.con-vs-loading {
    min-height: 24px;
    height: 24px;
    width: 24px;
    position: relative;
  }
}
</style>